import i18n from '@/i18n'
import { ErrorResponse, FileType, LanguageItem, SendEmailData, ServerResponse, TableSettings } from '@/types'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { authActions } from './auth/auth.slice'

const API_URL = process.env.REACT_APP_API_URL

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    headers.set('Accept', 'application/json')
    const token = localStorage.getItem('accessToken')
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    return headers
  },
})

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError & ErrorResponse> = async (args, api, extraOptions): Promise<any> => {
  let newArgs = args

  if (typeof newArgs === 'string') {
    const params = new URLSearchParams(newArgs.includes('?') ? newArgs.slice(newArgs.indexOf('?')) : '')
    const hasLangParam = params.has('lang')

    if (!hasLangParam) {
      params.set('lang', i18n.language)

      newArgs = newArgs.includes('?') ? `${newArgs.slice(0, newArgs.indexOf('?'))}?${params.toString()}` : `${newArgs}?${params.toString()}`
    }
  } else {
    const params = new URLSearchParams(newArgs.params)
    const urlParams = new URLSearchParams(newArgs.url.includes('?') ? newArgs.url.slice(newArgs.url.indexOf('?')) : '')
    const hasLangParam = params.has('lang') || urlParams.has('lang')

    if (!hasLangParam) {
      params.set('lang', i18n.language)

      newArgs = { ...newArgs, params }
    }
  }

  let result = await baseQuery(newArgs, api, extraOptions)

  if (result.error && result.error.status === 401) {
    localStorage.removeItem('accessToken')
    api.dispatch(authActions.setAuth(false))

    // try to get a new token
    // const refreshResult = await baseQuery('/tokens/refresh', api, extraOptions)
    // if (refreshResult.data) {
    // store the new token
    // localStorage.setItem('token', token)
    // api.dispatch(tokenReceived(refreshResult.data))
    // retry the initial query
    // result = await baseQuery(args, api, extraOptions)
    // } else {
    // api.dispatch(loggedOut())
    // }
  }
  return result
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'usersGroup',
    'usersPermissions',
    'usersDivisions',
    'categories',
    'countries',
    'clientGroups',
    'languages',
    'deliveryServices',
    'warehouses',
    'products',
    'productCharacteristics',
    'characteristics',
    'characteristicById',
    'characteristicsGroup',
    'characteristicsGroups',
    'manufacturersList',
    'manufacturer',
    'allClients',
    'reputation',
    'notes',
    'allClientsAddresses',
    'ordersStatuses',
    'admins',
    'adminById',
    'sizeCharts',
    'tableSettings',
    'priceGroups',
    'banners',
    'banner',
    'bannerImages',
    'productOffer',
    'staticPages',
    'staticPagesCommon',
    'staticPagesData',
    'ordersList',
    'paymentServices',
    'novaposhta',
    'labels',
    'allClientsCart',
    'orderDetail',
    'orderProductList',
    'productReviews',
    'siteReviews',
    'videoReviews',
    'customFields',
    'postReferences',
    'carts',
    'contents',
    'news',
    'documents',
    'ukrposhta',
    'allClientsFavourites',
    'promotions',
    'coupons',
    'leads',
    'mailingEmailTemplate',
    'mailingEmailList',
    'mailingSmsTemplate',
    'mailingSmsList',
    'mailingSegment',
  ],
  endpoints: (builder) => ({
    getTableSettings: builder.query<TableSettings[], string>({
      query: (name) => ({
        url: 'table_settings',
        params: {
          table: name,
        },
      }),
      providesTags: ['tableSettings'],
      transformResponse: (response: ServerResponse<TableSettings[]>) => response.data,
    }),
    getLanguages: builder.query<LanguageItem[], any>({
      query: () => ({
        url: 'languages/list',
      }),
      transformResponse: (response: ServerResponse<LanguageItem[]>) => response.data,
    }),
    addFile: builder.mutation<ServerResponse<FileType>, FormData>({
      query: (file) => ({
        url: 'add/file',
        method: 'POST',
        body: file,
      }),
    }),
    sendSms: builder.mutation<any, any>({
      query: (data) => ({
        url: '/sms/send',
        method: 'POST',
        body: data,
      }),
    }),
    getSendEmailData: builder.query<SendEmailData, { id: number; orderId?: number }>({
      query: ({ id, ...params }) => ({
        url: `/clients/${id}/email/template`,
        params,
      }),
      transformResponse: (response: ServerResponse<SendEmailData>) => ({
        ...response.data,
        senders: response.data.senders.map((i, index) => ({ ...i, id: index + 1 })),
      }),
    }),
    sendEmail: builder.mutation<any, any>({
      query: (data) => ({
        url: '/emails/send',
        method: 'POST',
        body: data,
      }),
    }),
  }),
})

export const { useGetTableSettingsQuery, useGetLanguagesQuery, useAddFileMutation, useSendSmsMutation, useGetSendEmailDataQuery, useSendEmailMutation } = api
